import {
  useEffect,
  useState,
  useContext
} from 'react';

import {
  addDoc,
  collection,
  doc,
  getDoc,
  updateDoc,
  serverTimestamp
} from 'firebase/firestore';
import { TokenContext } from "./tokenContext"; // import TokenContext
import { logout } from "../utils/firebase";
import { createPortal } from "react-dom";
import GlobalNavDrawer from "./GlobalNavDrawer";


import {
  getCandidateConversations,
  getMessagesForApplication,
  markConversationRead
} from '../services/messageService';

import { db } from '../utils/firebase';
import { useAuth } from './AuthContext';

import './CandidateProfile.css';
import CandidateResumeSection from "./CandidateResumeSection";
import CandidateCoachRelationshipsSection from './CandidateCoachRelationshipsSection';

function renderScore(score) {

  if (score > 0) {

    return (
      <span className="scoreNumber">
        {score}
      </span>
    );
  }

  return (
    <span className="scoreNotAssessed">
      Not Assessed
    </span>
  );
}

function calculateEmployabilityScore(profile) {
  const scores = [
    profile.resumeScore || 0,
    profile.interviewScore || 0,
    profile.communicationScore || 0
  ].filter(score => score > 0);

  if (scores.length === 0) {
    return null;
  }

  return Math.round(
    scores.reduce((a, b) => a + b, 0) /
    scores.length
  );
}

function calculateProfileCompletion(profile) {

  let score = 0;

  // Personal Information (20)
  if (profile.firstName) score += 4;
  if (profile.lastName) score += 4;
  if (profile.email) score += 4;
  if (profile.phone) score += 4;
  if (profile.city) score += 2;
  if (profile.province) score += 2;

  // Career Direction (25)
  if (profile.jobRole) score += 5;
  if (profile.primaryOccupation) score += 5;
  if (profile.secondaryOccupation) score += 2;
  if (profile.primaryEmploymentGoals) score += 8;
  if (profile.secondaryEmploymentGoals) score += 5;

  // Qualifications (25)
  if (
    Array.isArray(profile.certifications) &&
    profile.certifications.length > 0
  ) {
    score += 10;
  }

  if (
    Array.isArray(profile.licenses) &&
    profile.licenses.length > 0
  ) {
    score += 8;
  }

  if (
    Array.isArray(profile.education) &&
    profile.education.length > 0
  ) {
    score += 7;
  }

  // Readiness Assessments (30)
  score += Math.min(profile.resumeScore || 0, 100) * 0.10;
  score += Math.min(profile.interviewScore || 0, 100) * 0.10;
  score += Math.min(profile.communicationScore || 0, 100) * 0.10;

  return Math.round(Math.min(score, 100));
}

function CandidateProfile() {

  const {
    currentUser,
    loadingProfile
  } = useAuth();
  const { balance, setBalance } = useContext(TokenContext);

  useEffect(() => {
      let isMounted = true;
  
      const fetchTokens = async () => {
        if (!currentUser?.uid) return;
  
        const userRef = doc(db, "users", currentUser.uid);
  
        try {
          const userDoc = await getDoc(userRef);
  
          if (userDoc.exists() && isMounted) {
            setBalance(userDoc.data().tokens ?? 0);
          }
        } catch (error) {
          console.error("Error fetching token count:", error);
        }
      };
  
      fetchTokens();
  
      return () => {
        isMounted = false;
      };
    }, [currentUser, setBalance]);
  const [profile, setProfile] =
    useState({
      firstName: '',
      lastName: '',
      email: '',
      jobRole: '',
      shareWithCareerCoaches: false,
      interestedInJobCarveOuts: 'No',
      preferredCarveOutHours: '',
      preferredSupervisionLevel: '',
      barriersToTraditionalEmployment: '',
      carveOutNotes: '',

      phone: '',
      city: '',
      province: '',

      primaryOccupation: '',
      secondaryOccupation: '',

      primaryEmploymentGoals: '',
      secondaryEmploymentGoals: '',

      certifications: [],
      licenses: [],
      education: [],

      resumeScore: 0,
      interviewScore: 0,
      communicationScore: 0,

      coachNotes: ''
    });

  const [certInput, setCertInput] =
    useState('');

  const [licenseInput, setLicenseInput] =
    useState('');

  const [educationInput, setEducationInput] =
    useState('');

  const [loaded, setLoaded] =
    useState(false);
  
  const openConversation =
    async (conversation) => {

      await markConversationRead(
        conversation.applicationId,
        'candidate'
      );

      setSelectedConversation(
        conversation
      );

      const messages =
        await getMessagesForApplication(
          conversation.applicationId
        );

      setConversationMessages(
        messages
      );

    };
  
  const [
    conversations,
    setConversations
  ] = useState([]);

  const [
    selectedConversation,
    setSelectedConversation
  ] = useState(null);

  const [
    conversationMessages,
    setConversationMessages
  ] = useState([]);



  useEffect(() => {

    async function loadUserProfile() {

      if (!currentUser) {
        return;
      }

      try {

        const userRef =
          doc(
            db,
            'users',
            currentUser.uid
          );

        const snap =
          await getDoc(userRef);

        if (snap.exists()) {

          const data =
            snap.data();

          setProfile({
            firstName:
              data.firstName || '',

            lastName:
              data.lastName || '',

            email:
              data.email ||
              currentUser.email ||
              '',

            jobRole:
              data.jobRole || '',

            shareWithCareerCoaches:
              data.shareWithCareerCoaches === true,

            interestedInJobCarveOuts:
              data.interestedInJobCarveOuts || 'No',

            preferredCarveOutHours:
              data.preferredCarveOutHours || '',

            preferredSupervisionLevel:
              data.preferredSupervisionLevel || '',

            barriersToTraditionalEmployment:
              data.barriersToTraditionalEmployment || '',

            carveOutNotes:
              data.carveOutNotes || '',

            phone:
              data.phone || '',

            city:
              data.city || '',

            province:
              data.province || '',

            primaryOccupation:
              data.primaryOccupation || '',

            secondaryOccupation:
              data.secondaryOccupation || '',

            primaryEmploymentGoals:
              data.primaryEmploymentGoals || '',

            secondaryEmploymentGoals:
              data.secondaryEmploymentGoals || '',

            certifications:
              Array.isArray(data.certifications)
                ? data.certifications
                : [],

            licenses:
              Array.isArray(data.licenses)
                ? data.licenses
                : Array.isArray(data.licences)
                  ? data.licences
                  : [],

            education:
              Array.isArray(data.education)
                ? data.education
                : [],

            resumeScore:
              data.resumeScore || 0,

            interviewScore:
              data.interviewScore || 0,

            communicationScore:
              data.communicationScore || 0,

            coachNotes:
              data.coachNotes || ''
          });
        }

        const candidateConversations =
          await getCandidateConversations();

        setConversations(
          candidateConversations
        );

      } catch (err) {

        console.error(
          'Failed to load user profile:',
          err
        );

      } finally {

        setLoaded(true);
      }
    }

    loadUserProfile();

  }, [currentUser]);

  function addListItem(fieldName, value, clearInput) {

    const cleaned =
      value.trim();

    if (!cleaned) {
      return;
    }

    const existingItems =
      profile[fieldName] || [];

    const alreadyExists =
      existingItems
        .map(item => item.toLowerCase())
        .includes(cleaned.toLowerCase());

    if (alreadyExists) {
      clearInput('');
      return;
    }

    setProfile(prev => ({
      ...prev,
      [fieldName]: [
        ...(prev[fieldName] || []),
        cleaned
      ]
    }));

    clearInput('');
  }

  function removeListItem(fieldName, itemToRemove) {

    setProfile(prev => ({
      ...prev,
      [fieldName]:
        (prev[fieldName] || [])
          .filter(item =>
            item !== itemToRemove
          )
    }));
  }

  async function requestJobCarveOut() {

    if (!currentUser) {
      alert('You must be logged in.');
      return;
    }

    try {
      await addDoc(
        collection(db, 'jobCarveOutRequests'),
        {
          candidateId: currentUser.uid,
          candidateEmail: profile.email || currentUser.email || '',
          candidateName: [profile.firstName, profile.lastName]
            .filter(Boolean)
            .join(' '),
          requesterType: 'Candidate',
          desiredRole: profile.jobRole || profile.primaryOccupation || '',
          preferredHours: profile.preferredCarveOutHours || '',
          preferredSupervisionLevel: profile.preferredSupervisionLevel || '',
          barriersToTraditionalEmployment:
            profile.barriersToTraditionalEmployment || '',
          carveOutNotes: profile.carveOutNotes || '',
          status: 'Requested',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        }
      );

      setProfile(prev => ({
        ...prev,
        interestedInJobCarveOuts: 'Yes'
      }));

      alert('Job carve-out request submitted.');
    } catch (error) {
      console.error('Unable to request job carve-out:', error);
      alert('Unable to submit job carve-out request.');
    }
  }

  async function handleSave() {

    if (!currentUser) {

      alert(
        'You must be logged in.'
      );

      return;
    }

    try {

      const employabilityScore =
        calculateEmployabilityScore(profile);

      const profileCompletion =
        calculateProfileCompletion(profile);

      const userRef =
        doc(
          db,
          'users',
          currentUser.uid
        );

      await updateDoc(
        userRef,
        {
          firstName:
            profile.firstName || '',

          lastName:
            profile.lastName || '',

          email:
            profile.email || '',

          jobRole:
            profile.jobRole || '',

          shareWithCareerCoaches:
            profile.shareWithCareerCoaches === true,

          interestedInJobCarveOuts:
            profile.interestedInJobCarveOuts || 'No',

          preferredCarveOutHours:
            profile.preferredCarveOutHours || '',

          preferredSupervisionLevel:
            profile.preferredSupervisionLevel || '',

          barriersToTraditionalEmployment:
            profile.barriersToTraditionalEmployment || '',

          carveOutNotes:
            profile.carveOutNotes || '',

          phone:
            profile.phone || '',

          city:
            profile.city || '',

          province:
            profile.province || '',

          primaryOccupation:
            profile.primaryOccupation || '',

          secondaryOccupation:
            profile.secondaryOccupation || '',

          primaryEmploymentGoals:
            profile.primaryEmploymentGoals || '',

          secondaryEmploymentGoals:
            profile.secondaryEmploymentGoals || '',

          certifications:
            profile.certifications || [],

          licenses:
            profile.licenses || [],

          education:
            profile.education || [],

          coachNotes:
            profile.coachNotes || '',

          resumeScore:
            profile.resumeScore || 0,

          interviewScore:
            profile.interviewScore || 0,

          communicationScore:
            profile.communicationScore || 0,

          employabilityScore:
            employabilityScore || 0,

          profileCompletion,

          updatedAt:
            serverTimestamp()
        }
      );

      alert(
        'Profile Saved'
      );

    } catch (err) {

      console.error(
        'Failed to save profile:',
        err
      );

      alert(
        'Unable to save profile.'
      );
    }
  }

  if (loadingProfile) {
    return (
      <div className="candidateProfile">
        Loading...
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className="candidateProfile">
        Please log in.
      </div>
    );
  }

  if (!loaded) {
    return (
      <div className="candidateProfile">
        Loading...
      </div>
    );
  }

  const profileCompletion =
    calculateProfileCompletion(profile);

  function formatTimestamp(value) {

    if (!value) {
      return '';
    }

    const date =
      value?.toDate
        ? value.toDate()
        : new Date(value);

    if (isNaN(date.getTime())) {
      return '';
    }

    return date.toLocaleString([], {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });

  }
  
  return (

    <div className="candidateProfile">

      <h2>
        Client Profile
      </h2>

      <div className="readinessPanel">

        <div className="scoreCard">
            <div className="scoreTitle">
                <span>Resume </span>
                <span>Readiness</span>
            </div>
          <div className="scoreValue">
            {renderScore(profile.resumeScore)}
          </div>
        </div>

        <div className="scoreCard">
            <div className="scoreTitle">
                <span>Interview </span>
                <span>Readiness</span>
            </div>
          <div className="scoreValue">
            {renderScore(profile.interviewScore)}
          </div>
        </div>

        <div className="scoreCard">
            <div className="scoreTitle">
                <span>Communication </span>
                <span>Readiness</span>
            </div>
          <div className="scoreValue">
            {renderScore(profile.communicationScore)}
          </div>
        </div>

        <div className="scoreCard">
            <div className="scoreTitle">
                <span>Employability </span>
                <span>Score</span>
            </div>
          <div className="scoreValue">
            {renderScore(profile.employabilityScore)}
          </div>
        </div>

      </div>

        <div className="employabilitySection">

        <h3 className="profileProgressTitle">
            Client Readiness
        </h3>

        <div className="employabilityBar">
            <div
            className="employabilityFill"
            style={{ width: `${profileCompletion}%` }}
            />
        </div>

        <div className="employabilityLabel">
            {profileCompletion}% Complete
        </div>

        </div>

      <div className="profileSection">

        <h3>
          Personal Information
        </h3>

        <div className="profileGrid">

          <div className="profileField">
            <label>First Name</label>
            <input
              value={profile.firstName}
              onChange={(e) =>
                setProfile({
                  ...profile,
                  firstName: e.target.value
                })
              }
            />
          </div>

          <div className="profileField">
            <label>Last Name</label>
            <input
              value={profile.lastName}
              onChange={(e) =>
                setProfile({
                  ...profile,
                  lastName: e.target.value
                })
              }
            />
          </div>

          <div className="profileField">
            <label>Email</label>
            <input
              value={profile.email}
              onChange={(e) =>
                setProfile({
                  ...profile,
                  email: e.target.value
                })
              }
            />
          </div>

          <div className="profileField">
            <label>Job Role</label>
            <input
              value={profile.jobRole}
              onChange={(e) =>
                setProfile({
                  ...profile,
                  jobRole: e.target.value
                })
              }
            />
          </div>

          <div className="profileField">
            <label>Phone</label>
            <input
              value={profile.phone}
              onChange={(e) =>
                setProfile({
                  ...profile,
                  phone:
                    e.target.value
                })
              }
            />
          </div>

          <div className="profileField">
            <label>City</label>
            <input
              value={profile.city}
              onChange={(e) =>
                setProfile({
                  ...profile,
                  city:
                    e.target.value
                })
              }
            />
          </div>

          <div className="profileField">
            <label>Province</label>
            <input
              value={profile.province}
              onChange={(e) =>
                setProfile({
                  ...profile,
                  province:
                    e.target.value
                })
              }
            />
          </div>

          <div className="profileField">
            <label>Career Coach Visibility</label>
            <select
              value={profile.shareWithCareerCoaches ? 'Yes' : 'No'}
              onChange={(e) =>
                setProfile({
                  ...profile,
                  shareWithCareerCoaches: e.target.value === 'Yes'
                })
              }
            >
              <option>No</option>
              <option>Yes</option>
            </select>
          </div>

        </div>

      </div>

      <div className="profileSection">

        <h3>
          Employment Goals
        </h3>

        <div className="profileGrid">

          <div className="profileField">
            <label>Primary Occupation</label>
            <input
              value={profile.primaryOccupation}
              onChange={(e) =>
                setProfile({
                  ...profile,
                  primaryOccupation:
                    e.target.value
                })
              }
            />
          </div>

          <div className="profileField">
            <label>Secondary Occupation</label>
            <input
              value={profile.secondaryOccupation}
              onChange={(e) =>
                setProfile({
                  ...profile,
                  secondaryOccupation:
                    e.target.value
                })
              }
            />
          </div>

        </div>

        <div className="profileField">
          <label>Primary Employment Goal</label>
          <textarea
            value={profile.primaryEmploymentGoals}
            onChange={(e) =>
              setProfile({
                ...profile,
                primaryEmploymentGoals:
                  e.target.value
              })
            }
          />
        </div>

        <div className="profileField">
          <label>Secondary Employment Goal</label>
          <textarea
            value={profile.secondaryEmploymentGoals}
            onChange={(e) =>
              setProfile({
                ...profile,
                secondaryEmploymentGoals:
                  e.target.value
              })
            }
          />
        </div>

      </div>

      <div className="profileSection jobCarveOutRequestSection">

        <h3>
          Job Carve-Out Request
        </h3>

        <p className="carveOutHelpText">
          Use this section if a traditional job may not fit your availability,
          support needs, task strengths, or benefit limitations. A job carve-out
          can help employers shape a customized role around meaningful work.
        </p>

        <div className="profileGrid">

          <div className="profileField">
            <label>Interested in Job Carve-Outs?</label>
            <select
              value={profile.interestedInJobCarveOuts}
              onChange={(e) =>
                setProfile({
                  ...profile,
                  interestedInJobCarveOuts: e.target.value
                })
              }
            >
              <option>No</option>
              <option>Yes</option>
              <option>Maybe</option>
            </select>
          </div>

          <div className="profileField">
            <label>Preferred Hours Per Week</label>
            <input
              value={profile.preferredCarveOutHours}
              placeholder="Example: 10-15"
              onChange={(e) =>
                setProfile({
                  ...profile,
                  preferredCarveOutHours: e.target.value
                })
              }
            />
          </div>

          <div className="profileField">
            <label>Preferred Supervision Level</label>
            <select
              value={profile.preferredSupervisionLevel}
              onChange={(e) =>
                setProfile({
                  ...profile,
                  preferredSupervisionLevel: e.target.value
                })
              }
            >
              <option value="">Select one</option>
              <option>Independent after training</option>
              <option>Light check-ins</option>
              <option>Regular coaching</option>
              <option>Close supervision</option>
            </select>
          </div>

        </div>

        <div className="profileField">
          <label>Barriers to Traditional Employment</label>
          <textarea
            value={profile.barriersToTraditionalEmployment}
            placeholder="Example: transportation, AISH hours, sensory environment, stamina, communication, anxiety, task switching..."
            onChange={(e) =>
              setProfile({
                ...profile,
                barriersToTraditionalEmployment: e.target.value
              })
            }
          />
        </div>

        <div className="profileField">
          <label>Job Carve-Out Notes</label>
          <textarea
            value={profile.carveOutNotes}
            placeholder="Describe strengths, ideal tasks, helpful accommodations, and roles that may work."
            onChange={(e) =>
              setProfile({
                ...profile,
                carveOutNotes: e.target.value
              })
            }
          />
        </div>

        <button
          type="button"
          className="saveProfileButton"
          onClick={requestJobCarveOut}
        >
          Request Job Carve-Out
        </button>

      </div>

      <ProfileBadgeSection
        title="Certifications"
        fieldName="certifications"
        inputValue={certInput}
        setInputValue={setCertInput}
        placeholder="Example: WHMIS"
        profile={profile}
        addListItem={addListItem}
        removeListItem={removeListItem}
      />

      <ProfileBadgeSection
        title="Licenses"
        fieldName="licenses"
        inputValue={licenseInput}
        setInputValue={setLicenseInput}
        placeholder="Example: Class 1"
        profile={profile}
        addListItem={addListItem}
        removeListItem={removeListItem}
      />

      <ProfileBadgeSection
        title="Education"
        fieldName="education"
        inputValue={educationInput}
        setInputValue={setEducationInput}
        placeholder="Example: Bachelor of Arts"
        profile={profile}
        addListItem={addListItem}
        removeListItem={removeListItem}
      />

      <CandidateCoachRelationshipsSection
        currentUser={currentUser}
        userData={profile}
      />

      <div className="profileSection">

        <h3>
          Application Conversation History
        </h3>

        <p>
          Existing application conversations are shown as read-only history.
        </p>

        <div className="candidateMessages">
          <div className="conversationList">
            {conversations.map(conversation => (
              <button
                key={conversation.id}
                className="conversationListItem"
                onClick={() => openConversation(conversation)}
              >
                <div className="conversationCompany">{conversation.company}</div>
                <div className="conversationJob">{conversation.jobTitle}</div>
                <div className="conversationPreview">{conversation.lastMessage || 'No messages yet'}</div>
              </button>
            ))}
          </div>

          {selectedConversation && (
            <div className="conversationPanel">
              <h4>{selectedConversation.company}</h4>
              <div className="conversationMessages">
                {conversationMessages.map(message => (
                  <div key={message.id} className={`messageBubble ${message.senderType}`}>
                    <div className="messageSender">
                      {message.senderType === 'candidate'
                        ? 'You'
                        : message.senderType === 'employer'
                          ? selectedConversation.company
                          : 'System'}
                    </div>
                    <div className="messageText">{message.body}</div>
                    <div className="messageTime">{formatTimestamp(message.createdAt)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

      </div>

      <CandidateResumeSection />

      <div className="profileSection">

        <h3>
          Coach Notes
        </h3>

        <div className="profileField">
          <textarea
            value={profile.coachNotes}
            onChange={(e) =>
              setProfile({
                ...profile,
                coachNotes:
                  e.target.value
              })
            }
          />
        </div>

      </div>

      <button
        className="saveProfileButton"
        onClick={handleSave}
      >
        Save Profile
      </button>
        {createPortal(
          <GlobalNavDrawer
            hideCurrent
            links={[
              { to: "/purchase", label: `Tokens: ${balance}`},
              { to: "/jobbank", label: "Job Bank Home"},
              { to: "/voicebot", label: "iSpeakWell" },
              { to: "/resumerevisor", label: "Resumes" },
              { to: "/conversationpractice", label: "Convos" },
              { to: "/interview-practice", label: "Interviews" },
              { to: "/termsofservice", label: "Terms" },
              { to: "/privacypolicy", label: "Privacy" },
              { to: "/admin", label: "Admin" },
              { to: "/login", label: "Logout", onClick: async () => { await logout(); } },
            ]}
          />,
            document.body
        )}
    </div>
  );
}

function ProfileBadgeSection({
  title,
  fieldName,
  inputValue,
  setInputValue,
  placeholder,
  profile,
  addListItem,
  removeListItem
}) {

  return (

    <div className="profileSection">

      <h3>
        {title}
      </h3>

      <div className="profileGrid">

        <div className="profileField">
          <label>
            Add {title}
          </label>

          <input
            value={inputValue}
            placeholder={placeholder}
            onChange={(e) =>
              setInputValue(
                e.target.value
              )
            }
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();

                addListItem(
                  fieldName,
                  inputValue,
                  setInputValue
                );
              }
            }}
          />
        </div>

        <button
          type="button"
          className="saveProfileButton certificationAddButton"
          onClick={() =>
            addListItem(
              fieldName,
              inputValue,
              setInputValue
            )
          }
        >
          Add {title}
        </button>

      </div>

      <div className="certifications">

        {(profile[fieldName] || [])
          .map(item => (

            <span
              key={item}
              className="certBadge"
            >

              <span className="certIcon">
                🏅
              </span>

              <span className="certName">
                {item}
              </span>

              <button
                type="button"
                className="certRemove"
                onClick={() =>
                  removeListItem(
                    fieldName,
                    item
                  )
                }
              >
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 10 10"
                >
                  <path
                    d="M1 1L9 9M9 1L1 9"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>

            </span>

          ))}

      </div>
    </div>
  );
}

export default CandidateProfile;